import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { Theme, ThemeOptions } from '@mui/material/styles';
import './theme.d.ts';

// Device breakpoints based on common device sizes
const breakpoints = {
   values: {
      xs: 0, // Mobile phones in portrait
      sm: 600, // Mobile phones in landscape, small tablets
      md: 960, // Tablets, small laptops
      lg: 1280, // Laptops, desktops
      xl: 1920, // Large desktops
   },
};

// Spacing system optimized for mobile-first design
// Base unit: 8px (common in Material Design)
const spacing = 8;

// Typography scale that works well across device sizes
const typography = {
   fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
   // Mobile-optimized font sizes that scale appropriately
   h1: {
      fontSize: '2.5rem', // 40px on mobile
      '@media (min-width:600px)': {
         fontSize: '3rem', // 48px on tablets/desktop
      },
      fontWeight: 700,
      lineHeight: 1.2,
   },
   h2: {
      fontSize: '2rem', // 32px on mobile
      '@media (min-width:600px)': {
         fontSize: '2.5rem', // 40px on tablets/desktop
      },
      fontWeight: 700,
      lineHeight: 1.2,
   },
   h3: {
      fontSize: '1.5rem', // 24px on mobile
      '@media (min-width:600px)': {
         fontSize: '1.75rem', // 28px on tablets/desktop
      },
      fontWeight: 600,
      lineHeight: 1.3,
   },
   h4: {
      fontSize: '1.25rem', // 20px on mobile
      '@media (min-width:600px)': {
         fontSize: '1.5rem', // 24px on tablets/desktop
      },
      fontWeight: 600,
      lineHeight: 1.3,
   },
   h5: {
      fontSize: '1.1rem', // 17.6px on mobile
      '@media (min-width:600px)': {
         fontSize: '1.25rem', // 20px on tablets/desktop
      },
      fontWeight: 500,
      lineHeight: 1.4,
   },
   h6: {
      fontSize: '1rem', // 16px on mobile
      '@media (min-width:600px)': {
         fontSize: '1.125rem', // 18px on tablets/desktop
      },
      fontWeight: 500,
      lineHeight: 1.4,
   },
   body1: {
      fontSize: '1rem', // 16px
      lineHeight: 1.5,
   },
   body2: {
      fontSize: '0.875rem', // 14px
      lineHeight: 1.5,
   },
   button: {
      fontSize: '0.875rem', // 14px
      textTransform: 'none' as const, // Use 'as const' to make TypeScript treat this as a literal
      fontWeight: 500,
   },
   caption: {
      fontSize: '0.75rem', // 12px
      lineHeight: 1.5,
   },
};

// Components overrides for mobile optimization and gradient theme
const components = {
   MuiButton: {
      styleOverrides: {
         root: {
            borderRadius: 8,
            padding: '10px 16px', // More touch-friendly padding
            '@media (max-width:600px)': {
               padding: '12px 16px', // Even more padding on mobile for touch
            },
         },
         containedPrimary: {
            background: 'linear-gradient(135deg, #01949A 0%, #004369 100%)',
            boxShadow: '0 4px 15px rgba(1, 148, 154, 0.3)',
            '&:hover': {
               background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
               boxShadow: '0 6px 20px rgba(1, 148, 154, 0.4)',
            },
            '&:active': {
               background: 'linear-gradient(135deg, #005B60 0%, #001A2A 100%)',
            },
         },
         sizeLarge: {
            padding: '14px 22px',
            '@media (max-width:600px)': {
               padding: '16px 22px',
            },
         },
         sizeSmall: {
            padding: '6px 10px',
            '@media (max-width:600px)': {
               padding: '8px 10px',
            },
         },
      },
   },
   MuiCardContent: {
      styleOverrides: {
         root: {
            padding: 16,
            '@media (max-width:600px)': {
               padding: 16, // Consistent padding on mobile
            },
            '&:last-child': {
               paddingBottom: 16,
            },
         },
      },
   },
   MuiCardActions: {
      styleOverrides: {
         root: {
            padding: '12px 16px',
         },
      },
   },
   MuiDialog: {
      styleOverrides: {
         paper: {
            borderRadius: 12,
            '@media (max-width:600px)': {
               margin: 16,
               width: 'calc(100% - 32px)',
               maxWidth: 'calc(100% - 32px)',
            },
         },
      },
   },
   MuiDialogContent: {
      styleOverrides: {
         root: {
            padding: 24,
            '@media (max-width:600px)': {
               padding: 16, // Slightly less padding on mobile to maximize content space
            },
         },
      },
   },
   MuiDrawer: {
      styleOverrides: {
         paper: {
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
         },
      },
   },
   MuiListItem: {
      styleOverrides: {
         root: {
            '@media (max-width:600px)': {
               paddingTop: 12, // More touch-friendly padding
               paddingBottom: 12,
            },
         },
      },
   },
   MuiBottomNavigation: {
      styleOverrides: {
         root: {
            height: 65, // Taller bottom navigation as per your mobile optimization memory
         },
      },
   },
   MuiBottomNavigationAction: {
      styleOverrides: {
         root: {
            '& .MuiSvgIcon-root': {
               fontSize: '1.5rem', // Larger icons for better touch targets
            },
         },
      },
   },
   MuiAppBar: {
      styleOverrides: {
         root: {
            background: 'linear-gradient(135deg, #01949A 0%, #004369 100%)',
            boxShadow: '0 2px 10px rgba(1, 148, 154, 0.3)',
         },
      },
   },
   MuiChip: {
      styleOverrides: {
         colorPrimary: {
            background: 'linear-gradient(135deg, #01949A 0%, #004369 100%)',
            color: '#fff',
         },
      },
   },
   MuiLinearProgress: {
      styleOverrides: {
         colorPrimary: {
            background: 'linear-gradient(135deg, #01949A 0%, #004369 100%)',
         },
      },
   },
   MuiSwitch: {
      styleOverrides: {
         switchBase: {
            '&.Mui-checked': {
               color: '#01949A',
               '& + .MuiSwitch-track': {
                  background: 'linear-gradient(135deg, #01949A 0%, #004369 100%)',
               },
            },
         },
      },
   },
   MuiCheckbox: {
      styleOverrides: {
         root: {
            '&.Mui-checked': {
               color: '#01949A',
            },
         },
      },
   },
   MuiRadio: {
      styleOverrides: {
         root: {
            '&.Mui-checked': {
               color: '#01949A',
            },
         },
      },
   },
   MuiSlider: {
      styleOverrides: {
         thumb: {
            background: 'linear-gradient(135deg, #01949A 0%, #004369 100%)',
         },
         track: {
            background: 'linear-gradient(135deg, #01949A 0%, #004369 100%)',
         },
         rail: {
            background: 'rgba(1, 148, 154, 0.2)',
         },
      },
   },
   MuiTabs: {
      styleOverrides: {
         indicator: {
            background: 'linear-gradient(135deg, #01949A 0%, #004369 100%)',
         },
      },
   },
   MuiTab: {
      styleOverrides: {
         root: {
            '&.Mui-selected': {
               color: '#01949A',
            },
         },
      },
   },
   MuiFab: {
      styleOverrides: {
         primary: {
            background: 'linear-gradient(135deg, #01949A 0%, #004369 100%)',
            '&:hover': {
               background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
            },
         },
      },
   },
   MuiSpeedDial: {
      styleOverrides: {
         fab: {
            background: 'linear-gradient(135deg, #01949A 0%, #004369 100%)',
            '&:hover': {
               background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
            },
         },
      },
   },
   MuiTextField: {
      styleOverrides: {
         root: {
            '& .MuiInputBase-root': {
               borderRadius: 8,
            },
            '& .MuiOutlinedInput-root': {
               '&.Mui-focused fieldset': {
                  borderColor: '#01949A',
                  borderWidth: 2,
               },
            },
            '& .MuiInputLabel-root': {
               '&.Mui-focused': {
                  color: '#01949A',
               },
            },
         },
      },
   },
   MuiOutlinedInput: {
      styleOverrides: {
         root: {
            borderRadius: 8,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
               borderColor: '#01949A',
               borderWidth: 2,
            },
         },
         input: {
            padding: '14px 16px',
            '@media (max-width:600px)': {
               padding: '16px 16px',
            },
         },
      },
   },
   MuiFormControlLabel: {
      styleOverrides: {
         root: {
            '& .MuiCheckbox-root.Mui-checked': {
               color: '#01949A',
            },
            '& .MuiRadio-root.Mui-checked': {
               color: '#01949A',
            },
         },
      },
   },
   MuiLink: {
      styleOverrides: {
         root: {
            color: '#01949A',
            '&:hover': {
               color: '#004369',
            },
         },
      },
   },
   MuiIconButton: {
      styleOverrides: {
         root: {
            padding: 10,
            '@media (max-width:600px)': {
               padding: 12,
            },
            '&.MuiIconButton-colorPrimary': {
               color: '#01949A',
               '&:hover': {
                  backgroundColor: 'rgba(1, 148, 154, 0.1)',
               },
            },
         },
         sizeSmall: {
            padding: 8,
            '@media (max-width:600px)': {
               padding: 10,
            },
         },
      },
   },
   MuiPaper: {
      styleOverrides: {
         root: {
            borderRadius: 12,
            overflow: 'hidden',
         },
      },
   },
   MuiCard: {
      styleOverrides: {
         root: {
            borderRadius: 12,
            overflow: 'hidden',
            boxShadow: '0 2px 12px rgba(1, 148, 154, 0.08)',
            '&:hover': {
               boxShadow: '0 8px 30px rgba(1, 148, 154, 0.15)',
            },
         },
      },
   },
   MuiAlert: {
      styleOverrides: {
         standardInfo: {
            backgroundColor: 'rgba(1, 148, 154, 0.1)',
            color: '#01949A',
            '& .MuiAlert-icon': {
               color: '#01949A',
            },
         },
      },
   },
   MuiBadge: {
      styleOverrides: {
         colorPrimary: {
            backgroundColor: '#01949A',
         },
      },
   },
};

// Create base theme options
const themeOptions: ThemeOptions = {
   breakpoints,
   typography,
   components,
   spacing,
   palette: {
      primary: {
         main: '#01949A', // Teal
         light: '#2BB3B8',
         dark: '#007075',
         contrastText: '#fff',
      },
      secondary: {
         main: '#004369', // Navy Blue
         light: '#1A5F85',
         dark: '#002A42',
         contrastText: '#fff',
      },
      background: {
         default: '#E5DDC8', // Light Beige
         paper: '#F5F1E8',
      },
      text: {
         primary: '#004369', // Navy Blue for primary text
         secondary: '#6B7280', // Muted grey for secondary text
      },
      grey: {
         50: '#F9F7F4',
         100: '#F5F1E8',
         200: '#E5DDC8',
         300: '#D4C8A8',
         400: '#A89B7A',
         500: '#8B7E60',
         600: '#6B5E42',
         700: '#4A3F2A',
         800: '#2D2518',
         900: '#1A150C',
      },
      // Custom gradient colors for use throughout the app
      gradient: {
         primary: 'linear-gradient(135deg, #01949A 0%, #004369 100%)', // Teal to Navy
         primaryHover: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
         primaryActive: 'linear-gradient(135deg, #005B60 0%, #001A2A 100%)',
      },
      success: {
         main: '#10B981',
         light: '#34D399',
         dark: '#059669',
      },
      warning: {
         main: '#D38E2C', // Dark Yellow
         light: '#FBBF24',
         dark: '#B45309',
      },
      error: {
         main: '#BC0B25', // Red
         light: '#F87171',
         dark: '#991B1B',
      },
      info: {
         main: '#01949A', // Using teal for info as well
         light: '#2BB3B8',
         dark: '#007075',
      },
   },
   shape: {
      borderRadius: 8,
   },
};

// Create the base theme
let theme = createTheme(themeOptions);

// Apply responsive typography
theme = responsiveFontSizes(theme);

// Device detection utils
const deviceDetection = {
   isMobile: `@media (max-width:${theme.breakpoints.values.sm - 1}px)`,
   isTablet: `@media (min-width:${theme.breakpoints.values.sm}px) and (max-width:${theme.breakpoints.values.md - 1}px)`,
   isDesktop: `@media (min-width:${theme.breakpoints.values.md}px)`,
   isLargeDesktop: `@media (min-width:${theme.breakpoints.values.lg}px)`,
};

// Export as a utility function to ensure theme type safety
export const getResponsiveTheme = (): Theme => theme;

// Export device detection utils
export { deviceDetection };

export default theme;
